import {
	createRootRouteWithContext,
	type ErrorComponentProps,
	HeadContent,
	Scripts,
	useCanGoBack,
	useNavigate,
	useRouter
} from '@tanstack/react-router'
import { RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import '@/index.css'
import '@/lib/i18n'

import { Button } from '@repo/ui/button'
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from '@repo/ui/card'
import { AppToaster } from '@repo/ui/components/app-toaster'
import { BRAND_NAME } from '@repo/ui/components/marchio'
import { PictogramAccessDenied } from '@repo/ui/components/pictograms/access-denied'
import { PictogramFatalError } from '@repo/ui/components/pictograms/fatal-error'
import { PictogramSearchLens } from '@repo/ui/components/pictograms/search-lens'
import { Spinner } from '@repo/ui/spinner'
import { TooltipProvider } from '@repo/ui/tooltip'
import { formatError } from '@repo/ui/utils'
import { type TKey, translateApp } from '@/lib/i18n'
import { PermissionError } from '@/modules/permissions/lib/permission-error'
import type { RouterContext } from '@/router'

export const Route = createRootRouteWithContext<RouterContext>()({
	// Keep the document shell SSR-capable so SSR profiles can render <html>,
	// head tags and request language. This does not
	// force child routes to SSR; routes without their own `ssr` still follow
	// the active profile's `defaultSsr`.
	ssr: true,
	beforeLoad: ({ context, serverContext }) => {
		return {
			appLanguage: serverContext?.appLanguage ?? context.appLanguage
		}
	},
	head: () => ({
		meta: [
			{ charSet: 'utf-8' },
			{
				name: 'viewport',
				content: 'width=device-width, initial-scale=1.0'
			},
			{ title: BRAND_NAME }
		],
		links: [{ rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' }]
	}),
	shellComponent: RootDocument
})

function RootDocument({ children }: { children: ReactNode }) {
	const { appLanguage } = Route.useRouteContext()

	// Unicasa has a light theme only (DESIGN.md): no class, no switcher.
	return (
		<html
			className='h-full'
			lang={appLanguage}
			style={{ colorScheme: 'light' }}
		>
			<head>
				<HeadContent />
			</head>
			<body className='h-full'>
				<TooltipProvider>
					<AppToaster />
					{children}
				</TooltipProvider>
				<Scripts />
			</body>
		</html>
	)
}

export function NotFoundPage() {
	const { t } = useRouteTranslation()
	const navigate = useNavigate()

	return (
		<FallbackLayout
			actions={
				<Button onClick={() => navigate({ to: '/' })}>
					{t('common:app_fallbacks.go_home')}
				</Button>
			}
			description={t('common:app_fallbacks.not_found.description')}
			pictogram={<PictogramSearchLens size={180} />}
			title={t('common:app_fallbacks.not_found.title')}
		/>
	)
}

export function LoadingPage() {
	const { t } = useRouteTranslation()

	return (
		<div className='grid min-h-full w-full place-items-center p-4'>
			<Spinner
				aria-label={t('common:app_fallbacks.loading')}
				className='size-6 text-primary'
			/>
		</div>
	)
}

export function ErrorPage({ error, reset }: ErrorComponentProps) {
	const { appLanguage, t } = useRouteTranslation()
	const { handleGoBack } = useFallbackNavigation()

	if (error instanceof PermissionError) {
		return (
			<FallbackLayout
				actions={
					<Button onClick={handleGoBack} variant='outline'>
						{t('common:app_fallbacks.go_back')}
					</Button>
				}
				description={t(
					'common:app_fallbacks.permission_error.description'
				)}
				pictogram={<PictogramAccessDenied size={180} />}
				title={t('common:app_fallbacks.permission_error.title')}
			/>
		)
	}

	return (
		<FallbackLayout
			actions={
				<>
					<Button onClick={reset}>
						<RefreshCw data-icon='inline-start' />
						{t('common:app_fallbacks.try_again')}
					</Button>
					<Button onClick={handleGoBack} variant='outline'>
						{t('common:app_fallbacks.go_back')}
					</Button>
				</>
			}
			description={formatError(error, appLanguage)}
			pictogram={<PictogramFatalError size={180} />}
			title={t('common:app_fallbacks.error.title')}
		/>
	)
}

function useRouteTranslation() {
	const context = Route.useRouteContext()
	const { appLanguage } = context

	return {
		appLanguage,
		t: (key: TKey) => translateApp(appLanguage, key)
	}
}

/** Fills the route outlet, not the viewport: fallbacks render nested inside still-mounted parent layouts. */
function FallbackLayout({
	actions,
	description,
	pictogram,
	title
}: {
	actions: ReactNode
	description: string
	/** The drawing of the state: a lens for a missing page, a refused hand for a permission, a broken thing for a failure. */
	pictogram: ReactNode
	title: string
}) {
	return (
		<main className='grid min-h-full w-full place-items-center p-6'>
			<Card className='w-full max-w-xl bg-transparent shadow-none ring-0'>
				<CardHeader className='gap-6'>
					{pictogram}
					<CardTitle className='text-3xl'>{title}</CardTitle>
					<CardDescription className='max-w-lg text-base leading-7'>
						{description}
					</CardDescription>
				</CardHeader>
				<CardFooter className='flex-wrap gap-2'>{actions}</CardFooter>
			</Card>
		</main>
	)
}

function useFallbackNavigation() {
	const canGoBack = useCanGoBack()
	const navigate = useNavigate()
	const router = useRouter()

	return {
		handleGoBack: () => {
			if (canGoBack) {
				router.history.back()
				return
			}

			return navigate({ to: '/' })
		}
	}
}
